// Axios instance with JWT Bearer token authentication
import axios from 'axios';
import { getUserId } from './userIdManager';
import { getRegion } from './regionManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'allergen_app_jwt_token';

// Create axios instance
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

// In-flight promise to avoid multiple parallel token requests
let tokenInitPromise = null;

/**
 * Fetch a JWT token from the backend. Migrates an existing user_id
 * (from localStorage) so users don't lose their profiles/favorites.
 */
const fetchToken = async () => {
  const existingUserId = getUserId(); // Ensures a UUID exists in localStorage
  try {
    const response = await axios.post(
      `${BACKEND_URL}/api/auth/init`,
      { existing_user_id: existingUserId }
    );
    const { token } = response.data;
    localStorage.setItem(TOKEN_KEY, token);
    return token;
  } catch (err) {
    console.error('[api] Failed to initialize auth token', err);
    throw err;
  }
};

const getToken = () => localStorage.getItem(TOKEN_KEY);

const ensureToken = async () => {
  let token = getToken();
  if (token) return token;
  if (!tokenInitPromise) {
    tokenInitPromise = fetchToken().finally(() => {
      tokenInitPromise = null;
    });
  }
  return tokenInitPromise;
};

// Request interceptor: attach Bearer token
api.interceptors.request.use(
  async (config) => {
    // Skip auth for the init endpoint itself
    if (config.url && config.url.includes('/auth/init')) {
      return config;
    }
    const token = await ensureToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach region preference so backend can filter suggestions
    const region = getRegion();
    if (region) {
      config.headers = config.headers || {};
      config.headers['X-Country'] = region;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, clear token and retry once
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      localStorage.removeItem(TOKEN_KEY);
      try {
        const token = await ensureToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (e) {
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

export const clearAuthToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

export default api;
